type ClarityFn = (...args: unknown[]) => void;

const getClarity = (): ClarityFn | null => {
  if (typeof window === 'undefined') return null;
  const clarity = (window as unknown as {clarity?: unknown}).clarity;
  return typeof clarity === 'function' ? (clarity as ClarityFn) : null;
};

export const isClarityReady = (): boolean => getClarity() !== null;

type ClarityStub = ClarityFn & {q?: unknown[][]};

type ClarityWindow = {
  clarity?: ClarityStub;
  __clarityInjected?: boolean;
};

export const installClarityStub = (): void => {
  if (typeof window === 'undefined') return;
  const w = window as unknown as ClarityWindow;
  if (typeof w.clarity === 'function') return;
  const stub = function (...args: unknown[]) {
    (stub.q = stub.q || []).push(args);
  } as ClarityStub;
  w.clarity = stub;
};

export const ensureClarityLoaded = (projectId: string | undefined): void => {
  if (typeof window === 'undefined' || !projectId) return;
  const w = window as unknown as ClarityWindow;
  if (w.__clarityInjected) return;
  w.__clarityInjected = true;
  try {
    installClarityStub();
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.clarity.ms/tag/' + projectId;
    const first = document.getElementsByTagName('script')[0];
    first?.parentNode?.insertBefore(script, first);
  } catch (e) {
    w.__clarityInjected = false;
  }
};

/** Per-page A/B configuration, keyed by page pattern in `pageNames`. */
export type ClarityPageConfig = {
  /** Run the A/B test on this page. False (or absent) records every user. */
  enabled?: boolean;
  /**
   * Experiment driving this page. Ignored unless `enabled` is true, but
   * required when it is — `enabled` with no usable id disables Clarity on the
   * page entirely rather than recording an unsplit population.
   */
  experimentId?: number;
};

/**
 * Either the per-page map — `{"/registration": {enabled, experimentId}}` — or
 * the legacy flat list of patterns, in which case any A/B gating comes from the
 * sibling `abTest` block instead.
 */
export type ClarityPageNames = string[] | Record<string, ClarityPageConfig>;

/** Legacy single-experiment gate, used only when `pageNames` is a flat list. */
export type ClarityAbTest = {
  enabled?: boolean;
  experimentId?: number;
};

export type ClarityPlatformCriteria = {
  enableClarity?: boolean;
  pageNames?: ClarityPageNames;
  excludePageNames?: string[];
  abTest?: ClarityAbTest;
};

export type ClarityCriteria = ClarityPlatformCriteria & {
  platforms?: string[];
  hirist?: ClarityPlatformCriteria;
  iimjobs?: ClarityPlatformCriteria;
};

const matchesPattern = (entry: string, path: string): boolean => {
  const pattern = typeof entry === 'string' ? entry.trim() : '';
  if (!pattern) return false;
  if (pattern === '*' || pattern === '/*') return true;
  if (pattern.startsWith('*')) return path.endsWith(pattern.slice(1));
  if (pattern.endsWith('$')) return path === pattern.slice(0, -1);
  const prefix = pattern.endsWith('*') ? pattern.slice(0, -1) : pattern;
  return path.startsWith(prefix);
};

const matchesPath = (patterns: string[] | undefined, path: string): boolean => {
  if (!Array.isArray(patterns) || !patterns.length) return false;
  return patterns.some(entry => matchesPattern(entry, path));
};

const scopedCriteria = (
  criteria: ClarityCriteria | null | undefined,
  platform: string | undefined
): ClarityPlatformCriteria | undefined => {
  if (!criteria) return undefined;
  return platform === 'hirist' || platform === 'iimjobs'
    ? criteria[platform]
    : undefined;
};

const isPageMap = (
  pageNames: ClarityPageNames | undefined
): pageNames is Record<string, ClarityPageConfig> =>
  !!pageNames && !Array.isArray(pageNames) && typeof pageNames === 'object';

const pickPageNames = (
  criteria: ClarityCriteria | null | undefined,
  platform: string | undefined
): ClarityPageNames | undefined =>
  scopedCriteria(criteria, platform)?.pageNames ?? criteria?.pageNames;

/** Every configured pattern, whichever shape `pageNames` uses. */
const pagePatterns = (pageNames: ClarityPageNames | undefined): string[] => {
  if (isPageMap(pageNames)) return Object.keys(pageNames);
  return Array.isArray(pageNames) ? pageNames : [];
};

/** The entry governing `path`. Keys are matched in declaration order. */
const matchedPageConfig = (
  pageNames: ClarityPageNames | undefined,
  path: string
): ClarityPageConfig | undefined => {
  if (!isPageMap(pageNames)) return undefined;
  const key = Object.keys(pageNames).find(entry => matchesPattern(entry, path));
  return key === undefined ? undefined : pageNames[key];
};

const toExperimentId = (value: unknown): number => {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : 0;
};

const isClarityEnabled = (
  criteria: ClarityCriteria | null | undefined,
  platform: string | undefined
): boolean => {
  if (!criteria) return false;
  const enabled =
    scopedCriteria(criteria, platform)?.enableClarity ?? criteria.enableClarity;
  if (!enabled) return false;
  const {platforms} = criteria;
  if (Array.isArray(platforms) && platforms.length) {
    return !!platform && platforms.includes(platform);
  }
  return true;
};

/** Whether Clarity records at all on `path`. */
export const isClarityEligible = (
  criteria: ClarityCriteria | null | undefined,
  path: string,
  platform: string | undefined = process.env.NEXT_PUBLIC_APP_NAME
): boolean => {
  if (!isClarityEnabled(criteria, platform)) return false;

  const scoped = scopedCriteria(criteria, platform);
  const excluded = scoped?.excludePageNames ?? criteria?.excludePageNames;
  if (matchesPath(excluded, path)) return false;

  const pageNames = pickPageNames(criteria, platform);
  const included = pagePatterns(pageNames);
  if (included.length && !matchesPath(included, path)) return false;

  // A page that asks for an A/B test but names no usable experiment is
  // misconfigured. Recording it would silently mix both arms into one session
  // pool, so fail closed and record nothing until the flag is fixed.
  const config = matchedPageConfig(pageNames, path);
  if (config?.enabled && !toExperimentId(config.experimentId)) return false;

  return true;
};

/**
 * The experiment gating `path`, or 0 when Clarity should record every user
 * there. A page whose entry sets `enabled: false` still records — it simply
 * runs no A/B test. An entry that sets `enabled: true` without a usable
 * `experimentId` is misconfigured and records nobody; see `isClarityEligible`.
 */
export const resolveClarityAbExperiment = (
  criteria: ClarityCriteria | null | undefined,
  path: string,
  platform: string | undefined = process.env.NEXT_PUBLIC_APP_NAME
): number => {
  if (!isClarityEnabled(criteria, platform)) return 0;

  const pageNames = pickPageNames(criteria, platform);
  if (isPageMap(pageNames)) {
    const config = matchedPageConfig(pageNames, path);
    return config?.enabled ? toExperimentId(config.experimentId) : 0;
  }

  const abTest = scopedCriteria(criteria, platform)?.abTest ?? criteria?.abTest;
  return abTest?.enabled ? toExperimentId(abTest.experimentId) : 0;
};

/** Every experiment id needing a server-side variant assignment. */
export const listClarityAbExperimentIds = (
  criteria: ClarityCriteria | null | undefined,
  platform: string | undefined = process.env.NEXT_PUBLIC_APP_NAME
): number[] => {
  if (!isClarityEnabled(criteria, platform)) return [];

  const pageNames = pickPageNames(criteria, platform);
  if (isPageMap(pageNames)) {
    const ids = Object.values(pageNames)
      .filter(config => config?.enabled)
      .map(config => toExperimentId(config?.experimentId))
      .filter(Boolean);
    return Array.from(new Set(ids));
  }

  const abTest = scopedCriteria(criteria, platform)?.abTest ?? criteria?.abTest;
  const id = abTest?.enabled ? toExperimentId(abTest.experimentId) : 0;
  return id ? [id] : [];
};

export const readClarityCriteria = (): ClarityCriteria | undefined => {
  if (typeof window === 'undefined') return undefined;
  return (
    window as unknown as {
      __FEATURE_FLAGS__?: {clarityCriteria?: ClarityCriteria};
    }
  ).__FEATURE_FLAGS__?.clarityCriteria;
};

export type ClarityAbVariants = Record<string, string>;

export const readClarityAbVariants = (): ClarityAbVariants => {
  if (typeof window === 'undefined') return {};
  return (
    (window as unknown as {__CLARITY_AB_VARIANTS__?: ClarityAbVariants | null})
      .__CLARITY_AB_VARIANTS__ || {}
  );
};

export const readClarityAbVariant = (
  experimentId: number
): string | undefined =>
  readClarityAbVariants()[String(experimentId)] || undefined;

export const isClarityPathEligible = (path: string): boolean => {
  if (typeof window === 'undefined') return false;
  return isClarityEligible(readClarityCriteria(), path);
};

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: {timeout?: number}) => number;
};

let clarityLoadScheduled = false;

export const loadClarityWhenIdle = (projectId: string | undefined): void => {
  if (typeof window === 'undefined' || !projectId) return;
  if (clarityLoadScheduled) return;
  clarityLoadScheduled = true;

  installClarityStub();

  const w = window as IdleWindow;
  const inject = () => ensureClarityLoaded(projectId);
  const whenIdle = () => {
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(inject, {timeout: 3000});
    } else {
      window.setTimeout(inject, 1500);
    }
  };

  if (document.readyState === 'complete') {
    whenIdle();
  } else {
    window.addEventListener('load', whenIdle, {once: true, passive: true});
  }
};

export const clarityEvent = (eventName: string) => {
  const clarity = getClarity();
  if (!clarity || !eventName) return;
  try {
    clarity('event', eventName);
  } catch (e) {
    console.log('Clarity event error', e);
  }
};

export const claritySetTag = (key: string, value: string | string[]) => {
  const clarity = getClarity();
  if (!clarity || !key || value === undefined || value === null || value === '')
    return;
  try {
    clarity('set', key, value);
  } catch (e) {
    console.log('Clarity set tag error', e);
  }
};

export const clarityIdentify = (
  customId: string,
  customSessionId?: string,
  customPageId?: string,
  friendlyName?: string
) => {
  const clarity = getClarity();
  if (!clarity || !customId) return;
  try {
    clarity('identify', customId, customSessionId, customPageId, friendlyName);
  } catch (e) {
    console.log('Clarity identify error', e);
  }
};

const resolveClarityUserId = (): string => {
  try {
    const userId = sessionStorage.getItem('userId');
    if (userId && Number(userId) > 0) return userId;
  } catch {}
  return '';
};

let lastIdentifiedId = '';

export const clarityIdentifyUser = () => {
  if (!isClarityReady()) return;
  const id = resolveClarityUserId();
  if (!id || id === lastIdentifiedId) return;
  lastIdentifiedId = id;
  clarityIdentify(id, undefined, undefined, id);
};
